/**
 * Shared UV forecast line chart with navigation controls.
 *
 * Renders a 12-hour UV index chart using Recharts with Today/Now/Tomorrow
 * navigation buttons and left/right arrow scrolling. Supports click-to-select
 * for time picking, highlights the selected and current time points, and
 * adapts colors for light/dark themes.
 */
import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './UVChart.css';

const UVChart = ({
  chartData = [],
  graphMode = 'now',
  setGraphMode,
  graphOffset = 0,
  setGraphOffset,
  navigationLimits = { canGoLeft: false, canGoRight: false },
  onTimeSelect,
  onModeChange,
  selectedTimestamp,
  isDark = false,
}) => {
  const shiftLeft = () => {
    if (navigationLimits.canGoLeft) {
      setGraphOffset(graphOffset - 1);
    }
  };

  const shiftRight = () => {
    if (navigationLimits.canGoRight) {
      setGraphOffset(graphOffset + 1);
    }
  };

  const isDefaultView = (mode) => mode === graphMode && graphOffset === 0;

  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    let fill = '#ffd700';
    let r = 4;

    if (selectedTimestamp && Math.abs(payload.timestamp - selectedTimestamp) < 1800) {
      fill = '#00ff00';
      r = 6;
    } else if (payload.isCurrent) {
      fill = '#00ff00';
    }

    return <circle cx={cx} cy={cy} r={r} fill={fill} stroke={fill} strokeWidth={2} />;
  };

  const handleChartClick = (data) => {
    if (onTimeSelect && data && data.activePayload && data.activePayload[0]) {
      onTimeSelect(data.activePayload[0].payload.timestamp);
    }
  };

  const ActiveDot = (props) => {
    const { cx, cy, payload } = props;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={6}
        fill="#00ff00"
        stroke="#00ff00"
        strokeWidth={2}
        style={{ cursor: 'pointer' }}
        onClick={() => {
          if (onTimeSelect && payload) {
            onTimeSelect(payload.timestamp);
          }
        }}
      />
    );
  };

  return (
    <div className="uv-chart-wrapper">
      {/* Navigation Controls */}
      {setGraphMode && (
        <div className="uv-chart-navigation">
          <div className="uv-chart-navigation-row">
            <button
              className={`uv-chart-nav-arrow ${!navigationLimits.canGoLeft ? 'disabled' : ''}`}
              onClick={shiftLeft}
              disabled={!navigationLimits.canGoLeft}
            >
              ←
            </button>

            <div className="uv-chart-mode-buttons">
              <button
                className={`uv-chart-mode-button ${isDefaultView('today') ? 'active' : ''}`}
                onClick={() => { setGraphMode('today'); setGraphOffset(0); if (onModeChange) onModeChange('today'); }}
              >
                Today
              </button>
              <button
                className={`uv-chart-mode-button ${isDefaultView('now') ? 'active' : ''}`}
                onClick={() => { setGraphMode('now'); setGraphOffset(0); if (onModeChange) onModeChange('now'); }}
              >
                Now
              </button>
              <button
                className={`uv-chart-mode-button ${isDefaultView('tomorrow') ? 'active' : ''}`}
                onClick={() => { setGraphMode('tomorrow'); setGraphOffset(0); if (onModeChange) onModeChange('tomorrow'); }}
              >
                Tomorrow
              </button>
            </div>

            <button
              className={`uv-chart-nav-arrow ${!navigationLimits.canGoRight ? 'disabled' : ''}`}
              onClick={shiftRight}
              disabled={!navigationLimits.canGoRight}
            >
              →
            </button>
          </div>
        </div>
      )}

      {/* Chart */}
      {chartData.length > 0 ? (
        <div className="uv-chart-container">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData} onClick={handleChartClick} style={{ cursor: onTimeSelect ? 'pointer' : 'default' }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'} />
              <XAxis
                dataKey="time"
                stroke={isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.5)'}
                fontSize={10}
              />
              <YAxis
                domain={[0, 11]}
                stroke={isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.5)'}
                fontSize={10}
                width={25}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: isDark ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.95)',
                  border: '1px solid rgba(255,215,0,0.5)',
                  borderRadius: '6px',
                  color: isDark ? 'white' : 'black'
                }}
                formatter={(value, name, props) => {
                  const isCurrentHour = props.payload?.isCurrent;
                  const timeLabel = isCurrentHour ? 'Now' : '';
                  return [`${value} ${timeLabel}`, 'UV Index'];
                }}
                labelFormatter={(label, payload) => {
                  if (payload && payload[0] && payload[0].payload) {
                    const timestamp = payload[0].payload.timestamp;
                    const date = new Date(timestamp * 1000);
                    const currentDate = new Date();
                    const isToday = date.toDateString() === currentDate.toDateString();

                    if (isToday) {
                      return `Time: ${label}`;
                    } else {
                      const formattedTime = date.toLocaleTimeString("en-US", {
                        hour: "numeric",
                        hour12: true
                      });
                      const weekday = date.toLocaleDateString("en-US", {
                        weekday: "long"
                      });
                      return `Time: ${formattedTime} ${weekday}`;
                    }
                  }
                  return `Time: ${label}`;
                }}
              />
              <Line
                type="monotone"
                dataKey="uv"
                stroke="#ffd700"
                strokeWidth={2}
                dot={<CustomDot />}
                activeDot={<ActiveDot />}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="uv-chart-loading">Loading UV trend data...</div>
      )}
    </div>
  );
};

export default UVChart;
